import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Menu, Transition } from '@headlessui/react';
import moment from 'moment';
import {
	ArrowPathRoundedSquareIcon,
	ArrowUpTrayIcon,
	BellAlertIcon,
	BellIcon,
	CalendarDaysIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	ClipboardDocumentListIcon,
	ClockIcon,
	Cog6ToothIcon,
	CubeIcon,
	DocumentTextIcon,
	EllipsisHorizontalIcon,
	EllipsisVerticalIcon,
	InformationCircleIcon,
	PaperClipIcon,
	PencilSquareIcon,
	PlusCircleIcon,
	PlusIcon,
	TrashIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';

import ReactSelect, { components } from 'react-select';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import scipodsLogo from '../../static/images/scipods-logo-mark.png';

import { getModuleCategories, getModuleFieldsBeta } from '../api/modules';
import {
	deleteContract,
	getContract,
	removeDocumentFromContract,
	addDocumentToContract as apiAddDocumentsToContract,
	updateContract,
	uploadDocumentsToContract,
	editSingleContractData,
	createSingleContractData,
	updateSingleContractData,
	deleteSingleContractData,
	getLocations,
	addAssetToContract,
	removeAssetFromContract,
} from '../api';
import { getPMSchedules, updatePMSchedule } from '../api/pmSchedules';

import { AvatarGenerated } from '../components/Avatar';
import Button from '../components/Button';
import {
	StackableModal,
	AssetSelectorBeta,
	AddAttachmentButton,
	StatusBadge,
	FileUpload,
	DatePicker,
	FilterDropdown,
} from '../components/nextgen';
import { parseAssetTemplate } from '../components/nextgen/AssetSelectorBeta';
import Badge from '../components/Badge';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';

import {
	capitalizeFirstLetter,
	classNames,
	createDataTree,
	getUserFullName,
	objectAccess,
} from '../components/helpers/functions';

import BetaAttributes from '../components/Attributes';
import {
	AttributeRow,
	TemplateBoolean,
	TemplateCheckbox,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateFile,
	TemplateReactSelect,
} from '../components/Attributes/Attributes_old';
import DataTable from '../components/DataTable';
import { PageHeading } from '../components/Shell/Content';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../components/EllipsisDropdown';
import { Bars3Icon } from '@heroicons/react/20/solid';
import { getAssets } from '../api/assets';
import usePrevious from '../hooks/usePrevious';
import { Document, Page } from 'react-pdf';
import { getDocuments } from '../api/documents';
import { FormRow } from '../components/Form/tailwindForm';
import SettingsContext from '../context/SettingsContext';
import { getContacts } from '../api/contacts';
import { createReminder, deleteReminder, getReminders } from '../api/reminders';
import { DetailListOptions } from '../components/DetailList/DetailList';
import Banner from '../components/Banner';

const MODAL_NAME = 'contractOverview';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;
const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const statuses = {
	snooze: 'text-yellow-500 bg-yellow-100/10',
	active: 'text-green-400 bg-green-400/10',
	error: 'text-rose-400 bg-rose-400/10',
};

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<ChevronDownIcon className="w-4 h-4 text-gray-500" />
		</components.DropdownIndicator>
	);
};

const ClearIndicator = (props) => {
	return (
		<components.ClearIndicator {...props}>
			<XMarkIcon className="w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer" />
		</components.ClearIndicator>
	);
};

const CustomMenuList = ({ customAction, ...props }) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{customAction ? (
				<>
					<div className="border-t border-gray-200">
						<Button
							type="ghost"
							Icon={PlusIcon}
							onClick={() => customAction?.onClick()}
						>
							{customAction?.label}{' '}
						</Button>
					</div>
				</>
			) : null}
		</components.MenuList>
	);
};

// This is used to find a nested object by ID and return the parent objects
function findPathById(items, targetId, path = []) {
	for (const item of items) {
		const newPath = [...path, item];
		if (item.id === targetId) {
			return newPath;
		}
		if (item.children) {
			const result = findPathById(item.children, targetId, newPath);
			if (result) return result;
		}
	}
	return null;
}

const ContractOverview = () => {
	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const navigate = useNavigate();
	const { open, setClose } = useModal(MODAL_NAME);

	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');
	const { setOpen: openDeleteContractModal } = useModal('deleteContract');
	const { setOpen: openNewVendor } = useModal('addVendorModal');

	const { open: isNewTaskModalOpen, setOpen: openNewTaskModal } =
		useModal('taskAdd');

	const { open: selectLocationOptions, setOpen: openSelectLocation } =
		useModal('selectLocation');

	const { setOpen: openContactModal } = useModal('viewContact');
	const { open: isUploadDocumentsOpen, setOpen: openUploadDocuments } =
		useModal('uploadDocuments');

	const [renewForm, setRenewForm] = useState({});
	const [documentUploadForm, setDocumentUploadForm] = useState({});
	const [addAssetModal, setAddAssetModal] = useState(false);
	const [selectedAssetsToAdd, setSelectedAssetsToAdd] = useState([]);
	const [addDocumentToContract, setAddDocumentToContract] = useState(false);

	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');
	const { setOpen: onOpenContactModal } = useModal('viewContact');

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');
	const { setOpen: openDocumentPreview, setClose: closeDocumentPreview } =
		useModal('documentPreview');
	const { setOpen: openPMScheduleOverview } = useModal('pmScheduleOverview');
	const { setOpen: openEditPMSchedule } = useModal('editPMSchedule');
	const [confirmModal, setConfirmModal] = useState(false);
	const [reminderModal, setReminderModal] = useState(false);
	const [addReminderModal, setAddReminderModal] = useState(false);
	const [editContractModal, setEditContractModal] = useState(false);
	const [editContractForm, setEditContractForm] = useState({});
	const [selectExistingPMModal, setSelectExistingPMModal] = useState(false);
	const [confirmPMSwitch, setConfirmPMSwitch] = useState(null);

	const { setOpen: onOpenDeleteReminder, setClose: onCloseDeleteReminder } =
		useModal('deleteModal');

	// Fetch Contract
	const {
		value: contract,
		status,
		execute: refreshContract,
	} = useAsync(getContract, { immediate: false });

	// Fetch PM Schedules for this contract
	const { value: pmSchedules, execute: refreshPMSchedules } = useAsync(
		getPMSchedules,
		{ immediate: false },
	);

	// Fetch all PM Schedules (for selecting existing ones)
	const { value: allPMSchedules, execute: fetchAllPMSchedules } = useAsync(
		getPMSchedules,
		{ immediate: false },
	);

	// Update PM Schedule (to link to contract)
	const { execute: onUpdatePMSchedule } = useAsync(updatePMSchedule, {
		immediate: false,
	});

	const { value: reminders, execute: onRefreshReminders } = useAsync(
		getReminders,
		{ immediate: false },
	);

	const { execute: onCreateReminder } = useAsync(createReminder, {
		immediate: false,
	});

	const { execute: onDeleteReminder } = useAsync(deleteReminder, {
		immediate: false,
	});

	const { status: isAddingAssetToContract, execute: onAddAssetToContract } =
		useAsync(addAssetToContract, {
			immediate: false,
		});

	const { execute: onRemoveAssetFromContract } = useAsync(
		removeAssetFromContract,
		{
			immediate: false,
		},
	);

	const { value: assetsData, execute: onRefreshAssets } = useAsync(getAssets, {
		immediate: false,
	});

	// Process assets data for selector
	const assets = useMemo(() => {
		if (!assetsData?.rows) return [];
		return assetsData.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assetsData]);

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: CONTRACT_MODULE_ID, active: true },
	});

	const { execute: handleUpdateContract, status: updatingContract } = useAsync(
		updateContract,
		{ immediate: false },
	);

	const { execute: onRemoveDocument } = useAsync(removeDocumentFromContract, {
		immediate: false,
	});

	const { execute: onAddDocument } = useAsync(apiAddDocumentsToContract, {
		immediate: false,
	});

	const { execute: onCreateContractData } = useAsync(createSingleContractData, {
		immediate: false,
	});

	const { execute: onUpdateContractData } = useAsync(updateSingleContractData, {
		immediate: false,
	});

	const { execute: onDeleteContractData } = useAsync(deleteSingleContractData, {
		immediate: false,
	});

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshFields();
		}
	});

	useEffect(() => {
		if (open?.id) {
			refreshContract({ id: open?.id });
			refreshPMSchedules({ contractId: open?.id });
		} else {
			closeDeleteModal();
		}
	}, [open?.id]);

	const prevAddAssetModal = usePrevious(addAssetModal);
	useEffect(() => {
		if (addAssetModal && !prevAddAssetModal) {
			onRefreshAssets({ pageSize: null });
			setSelectedAssetsToAdd([]);
		} else if (!addAssetModal && prevAddAssetModal) {
			refreshContract({ id: open?.id }, null, { silent: true });
		}
	}, [addAssetModal]);

	const prevRemindersModal = usePrevious(reminderModal);
	useEffect(() => {
		if (reminderModal && !prevRemindersModal) {
			onRefreshReminders({ modelId: open?.id }, null, { silent: true });
		}
	}, [reminderModal]);

	const prevNewTaskModalOpen = usePrevious(isNewTaskModalOpen);
	useEffect(() => {
		if (!isNewTaskModalOpen && prevNewTaskModalOpen && open?.id) {
			refreshContract({ id: open?.id }, null, { silent: true });
		}
	}, [isNewTaskModalOpen, open?.id]);

	// Delete Contract
	const { execute: onDeleteContract } = useAsync(deleteContract, {
		immediate: false,
	});

	const { execute: onUpdateContract } = useAsync(updateContract, {
		immediate: false,
		showNotification: { success: 'Contract has been updated', error: 'raw' },
	});

	const { execute: onUploadDocuments, status: isUploadingDocument } = useAsync(
		uploadDocumentsToContract,
		{
			immediate: false,
		},
	);

	const { setOpen: openNewCategory } = useModal('addCategoryModal');

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});

	const {
		value: contractCategories,
		execute: onRefreshContractCategories,
		status: isLoadingContractCategories,
	} = useAsync(getModuleCategories, {
		defaultPayload: { id: CONTRACT_MODULE_ID },
	});

	const {
		value: documents,
		execute: onRefreshDocuments,
		status: isLoadingDocuments,
	} = useAsync(getDocuments, {
		immediate: false,
	});

	const { value: locations, status: isLoadingLocations } =
		useAsync(getLocations);

	const {
		value: vendors,
		status: isLoadingVendors,
		execute: refreshVendors,
	} = useAsync(getContacts);

	const prevContractModal = usePrevious(addDocumentToContract);
	useEffect(() => {
		if (!prevContractModal && addDocumentToContract) {
			onRefreshCategories({
				id: DOCUMENTS_MODULE_ID,
			});

			onRefreshDocuments();
		}

		if (prevContractModal && !addDocumentToContract) {
			setDocumentUploadForm({});
		}
	}, [addDocumentToContract]);

	const prevRenewModal = usePrevious(confirmModal);
	useEffect(() => {
		if (!prevRenewModal && confirmModal) {
			onRefreshCategories({
				id: DOCUMENTS_MODULE_ID,
			});
		}

		if (prevRenewModal && !confirmModal) {
			setRenewForm({});
		}
	}, [confirmModal]);

	// Tab State
	const [currentTab, setCurrentTab] = useState('tasks');
	const tabs = [
		{
			name: 'Tasks',
			onClick: () => setCurrentTab('tasks'),
			count: contract?.services?.length || 0,
			current: currentTab === 'tasks',
		},
		{
			name: 'Work Orders',
			onClick: () => setCurrentTab('workOrders'),
			count: contract?.workOrders?.length || 0,
			current: currentTab === 'workOrders',
		},
		{
			name: 'Assets',
			onClick: () => setCurrentTab('assets'),
			count: contract?.assets?.length || 0,
			current: currentTab === 'assets',
		},
		{
			name: 'Documents',
			onClick: () => setCurrentTab('documents'),
			count: contract?.documents?.length || 0,
			current: currentTab === 'documents',
		},
		{
			name: 'Timeline',
			onClick: () => setCurrentTab('timeline'),
			count: contract?.events?.length || 0,
			current: currentTab === 'timeline',
		},
	];

	const handleClose = (skipOnClose = false) => {
		if (!skipOnClose && typeof open?.onClose === 'function') {
			open.onClose();
		}
		setClose();
		setConfirmModal(false);
		setReminderModal(false);
		setCurrentTab('assets');
	};

	// Loading Skeleton
	if (status === 'pending') {
		return <LoadingSkeleton />;
	}

	const CONVERTER = {
		'coverage-start': 'startDate',
	};

	const isInactive = contract?.active === false;
	const isOverdue = false;

	const isExpired = moment
		.utc(contract?.endDate)
		.isBefore(moment().startOf('day'));
	const isExpiringSoon = moment
		.utc(contract?.endDate)
		.isBefore(moment().add(30, 'days').endOf('day'));

	const allEvents = [
		...(contract?.services?.length
			? contract?.services
					?.flatMap((service) => service?.events)
					.filter((event) => event?.status === 'COMPLETED')
			: []),
		...(contract?.assets?.length
			? contract?.assets
					?.flatMap((asset) => asset?.events)
					.filter((event) => event?.status === 'COMPLETED')
			: []),
	];

	const categoryOptions = categories
		?.sort((a, b) => a.name?.localeCompare(b.name))
		.map((category) => ({
			label: category?.name,
			value: category.id,
			fields: category?.attribute_fields,
		}));

	const showcasePDF = contract?.documents?.length
		? contract?.documents?.find((doc) => doc?.id === contract?.activePdf)
		: null;

	const reducedAttributes = moduleFields?.combined
		?.filter((field) => field?.label !== 'Tags')
		?.reduce(
			(prev, cur) => {
				//if (form[cur?.key]?.value) {
				prev[cur?.isDefault ? 'default' : 'custom'].push(cur);
				//}
				return prev;
			},
			{ default: [], custom: [] },
		);

	const getPath = {
		manufacturer: {
			path: 'manufacturer.name',
		},
		model: {
			path: 'model.name',
		},
		location: {
			path: 'location.id',
		},
		category: {
			path: 'category.id',
		},
	};

	const getValue = (field, obj) => {
		return (obj || contract)?.custom_attributes?.find(
			(o) => o.attribute_field_id === field?.id,
		)?.value;
	};

	const contractCategoryOptions = contractCategories?.map((category) => ({
		label: category?.name,
		value: category?.id,
	}));
	const vendorOptions = vendors?.rows?.map((vendor) => ({
		label: vendor?.companyName || getUserFullName(vendor),
		value: vendor?.id,
	}));

	const booleanOptions = [
		{ label: 'True', value: 'true' },
		{ label: 'False', value: 'false' },
	];

	const locationOptionsTree = createDataTree(locations?.rows, 'children');

	const contractVendor = contract?.contacts?.length
		? contract?.contacts[0]?.id
		: null;

	const REMIND_ALL_ADMINS =
		settings?.company?.orgNotificationContractsExpiringAdmins;
	const REMIND_PRIMARY_USER =
		settings?.company?.orgNotificationContractsExpiringPrimary?.email;
	const assetDisplayTemplate = settings?.company?.assetDisplay;

	return (
		<>
			<StackableModal
				size="full"
				open={!!open}
				onClose={() => handleClose()}
				showCloseIcon={false}
			>
				{/* Header */}
				<div className="px-4 py-4 sm:px-6 lg:px-8 border-b border-gray-200 bg-white">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-x-4">
							<img
								alt=""
								src={scipodsLogo}
								className="size-16 flex-none rounded-full ring-1 ring-gray-900/10 p-4"
							/>
							<h1>
								<div className="text-sm/6 text-gray-500 text-left">
									{contract?.category?.name}
								</div>
								<div className="text-lg font-semibold text-gray-900">
									{`${isOverdue ? 'Expired: ' : ''} ${contract?.name}`}
								</div>
							</h1>
						</div>
						<div className="flex items-center gap-x-2">
							<Menu as="div" className="relative inline-block text-left">
								<Menu.Button as="div" className="cursor-pointer">
									<Button type="secondary" Icon={Cog6ToothIcon}>
										Settings
									</Button>
								</Menu.Button>
								<Menu.Items
									transition
									className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
								>
									{contract?.active !== false && (
										<div className="py-1">
											<Menu.Item>
												<a
													onClick={() => {
														setEditContractForm({
															name: contract?.name || '',
															startDate: contract?.startDate,
															endDate: contract?.endDate,
														});
														setEditContractModal(true);
													}}
													className="flex items-center px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none cursor-pointer hover:bg-gray-50"
												>
													<PencilSquareIcon
														aria-hidden="true"
														className="mr-3 size-5 text-gray-400"
													/>
													Edit Contract
												</a>
											</Menu.Item>
											<Menu.Item>
												<a
													onClick={() => {
														setConfirmModal(true);
														setRenewForm({
															endDate: moment(contract?.endDate)
																.add(1, 'year')
																.toDate(),
														});
													}}
													className="flex items-center px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none cursor-pointer hover:bg-gray-50"
												>
													<ArrowPathRoundedSquareIcon
														aria-hidden="true"
														className="mr-3 size-5 text-gray-400"
													/>
													Renew Contract
												</a>
											</Menu.Item>
										</div>
									)}
									<div className="py-1">
										<Menu.Item>
											<a
												onClick={() =>
													openDeleteContractModal({
														contractId: contract?.id,
														onSuccess: () => {
															handleClose();
															navigate('/manage/contracts');
														},
													})
												}
												className="flex items-center px-4 py-2 text-sm text-red-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none cursor-pointer hover:text-red-700 hover:font-medium"
											>
												<TrashIcon
													aria-hidden="true"
													className="mr-3 size-5 text-red-700 group-data-[focus]:text-gray-500"
												/>
												Delete Contract
											</a>
										</Menu.Item>
									</div>
								</Menu.Items>
							</Menu>

							<button
								onClick={() => handleClose()}
								className="ml-2 p-2 text-gray-400 hover:text-gray-500"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>
					</div>
				</div>

				{/* Voided Banner */}
				{contract?.active === false && (
					<Banner status="error" className="text-center">
						This contract has been voided and is no longer active
					</Banner>
				)}

				{/* Expired Banner */}
				{contract?.active !== false && isExpired && (
					<Banner status="warning" className="text-center">
						This contract has expired
					</Banner>
				)}

				{/* Main Content */}
				<div className="bg-gray-50 min-h-screen">
					<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
						{/* Contract Term Section */}
						{!isExpired && (
							<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
								<div className="flex items-center justify-between">
									<div className="flex-1">
										<div className="flex items-baseline gap-2">
											<span
												className={`text-4xl font-bold ${isExpired ? 'text-red-600' : 'text-gray-900'}`}
											>
												{isExpired
													? `${moment().diff(moment.utc(contract?.endDate), 'days')} days`
													: `${moment.utc(contract?.endDate).diff(moment(), 'days')} days`}
											</span>
										</div>
										<div className="text-sm text-gray-500 mt-1">
											{isExpired ? 'expired' : 'remaining'}
										</div>

										{/* Progress Bar */}
										<div className="mt-6">
											<div className="relative">
												<div className="h-2 bg-gray-200 rounded-full">
													<div
														className={`h-2 ${isExpired ? 'bg-red-500' : 'bg-indigo-500'} rounded-full`}
														style={{
															width: '100%',
														}}
													/>
												</div>
											</div>
											<div className="flex justify-between mt-2 text-xs text-gray-500">
												<span>
													Start ·{' '}
													{moment
														.utc(contract?.startDate)
														.format('MMM D, YYYY')}
												</span>
												<span>
													End ·{' '}
													{moment.utc(contract?.endDate).format('MMM D, YYYY')}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Stats Cards */}
						<div className="grid grid-cols-3 gap-4 mb-6">
							<div className="bg-white rounded-lg border border-gray-200 p-4">
								<div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
									<ClockIcon className="h-4 w-4" />
									<span>
										{isExpired ? 'Contract expired' : 'Term progress'}
									</span>
								</div>
								{isExpired ? (
									<>
										<div className="text-2xl font-bold text-red-600">
											{moment().diff(moment.utc(contract?.endDate), 'days')}{' '}
											days
										</div>
										<div className="text-sm text-gray-500">ago</div>
									</>
								) : (
									<>
										<div className="text-2xl font-bold text-gray-900">
											{Math.round(
												Math.min(
													100,
													Math.max(
														0,
														(moment().diff(
															moment.utc(contract?.startDate),
															'days',
														) /
															moment
																.utc(contract?.endDate)
																.diff(
																	moment.utc(contract?.startDate),
																	'days',
																)) *
															100,
													),
												),
											)}
											%
										</div>
										<div className="text-sm text-gray-500">elapsed</div>
									</>
								)}
							</div>
							<div className="bg-white rounded-lg border border-gray-200 p-4">
								<div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
									<CubeIcon className="h-4 w-4" />
									<span>Covered assets</span>
								</div>
								<div className="text-2xl font-bold text-gray-900">
									{contract?.assets?.length || 0}
								</div>
								<div className="text-sm text-gray-500">
									asset{contract?.assets?.length !== 1 ? 's' : ''}
								</div>
							</div>
							<div className="bg-white rounded-lg border border-gray-200 p-4">
								<div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
									<DocumentTextIcon className="h-4 w-4" />
									<span>Documents</span>
								</div>
								<div className="text-2xl font-bold text-gray-900">
									{contract?.documents?.length || 0}
								</div>
								<div className="text-sm text-gray-500">
									file{contract?.documents?.length !== 1 ? 's' : ''}
								</div>
							</div>
						</div>

						{/* Two Column Layout */}
						<div className="grid grid-cols-3 gap-6">
							{/* Left Column - 2/3 width */}
							<div className="col-span-2 space-y-6">
								{/* Contract Details */}
								<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center justify-between mb-4">
										<div className="flex items-center gap-2">
											<DocumentTextIcon className="h-5 w-5 text-gray-400" />
											<h3 className="text-base font-semibold text-gray-900">
												Contract Details
											</h3>
										</div>
										<button
											onClick={() =>
												openManageAttributes({
													moduleId: CONTRACT_MODULE_ID,
													onSelect: (payload) => {},
												})
											}
											className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
										>
											Manage fields
										</button>
									</div>

									<div>
										<ul
											role="list"
											className="divide-y divide-gray-100 text-xs grid grid-cols-2 gap-x-4"
										>
											<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
												<div className="grid grid-cols-2 gap-x-6 flex-1">
													<div className="text-gray-500 pt-2">Start date</div>
													<div className="">
														<div className="py-2.5 px-3 flex justify-between items-center">
															<span>
																{moment
																	.utc(contract?.startDate)
																	.format('MMM Do, YYYY')}
															</span>
														</div>
													</div>
												</div>
											</li>
											<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
												<div className="grid grid-cols-2 gap-x-6 flex-1">
													<div className="text-gray-500 pt-2">End date</div>
													<div className="">
														<div className="py-2.5 px-3 flex justify-between items-center">
															<span>
																{moment
																	.utc(contract?.endDate)
																	.format('MMM Do, YYYY')}
															</span>
														</div>
													</div>
												</div>
											</li>
											<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
												<div className="grid grid-cols-2 gap-x-6 flex-1">
													<div className="text-gray-500 pt-2">Vendor</div>
													<div className="">
														<ReactSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															noOptionsMessage={() => 'Not found'}
															components={{
																ClearIndicator,

																...(contractVendor
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
																MenuList: (props) => (
																	<CustomMenuList
																		{...props}
																		customAction={{
																			label: 'Add a vendor',
																			onClick: () =>
																				openNewVendor({
																					onSubmit: (vendor) => {
																						// Refresh vendor list to include newly added vendor
																						refreshVendors();
																						onUpdateContract(
																							{
																								id: contract?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: vendor?.value,
																									},
																								],
																							},
																							(payload) => {
																								refreshContract(
																									{
																										id: contract?.id,
																									},
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					},
																				}),
																		}}
																	/>
																),
															}}
															value={vendorOptions?.find(
																(o) => o.value === contractVendor,
															)}
															isDisabled={isLoadingVendors === 'pending'}
															options={vendorOptions}
															onChange={(newValue) => {
																onUpdateContract(
																	{
																		id: contract?.id,
																		vendor: newValue?.value || null,
																	},
																	(payload) => {
																		refreshContract(
																			{ id: contract?.id },
																			null,
																			{
																				silent: true,
																			},
																		);
																	},
																);
															}}
															isClearable
														/>
													</div>
												</div>
											</li>
											<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
												<div className="grid grid-cols-2 gap-x-6 flex-1">
													<div className="text-gray-500 pt-2">Category</div>
													<div className="">
														<DetailListOptions
															value={contract?.categoryId}
															options={contractCategoryOptions}
															onChange={(newValue) => {
																onUpdateContract(
																	{
																		id: contract?.id,
																		category: newValue?.value || null,
																	},
																	(payload) => {
																		refreshContract(
																			{ id: contract?.id },
																			null,
																			{
																				silent: true,
																			},
																		);
																	},
																);
															}}
														/>
													</div>
												</div>
											</li>
											{reducedAttributes?.custom?.length
												? reducedAttributes?.custom
														?.sort((a, b) =>
															a.label
																.toLowerCase()
																.localeCompare(b.label.toLowerCase()),
														)
														?.map((field) => {
															const value = getValue(field);
															const isEditable = !field?.isDefault;
															return (
																<>
																	<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
																		<div className="grid grid-cols-2 gap-x-6 flex-1">
																			<div className="text-gray-500 pt-2">
																				{field?.label}
																			</div>
																			<div className="">
																				{isEditable
																					? (() => {
																							switch (field?.type) {
																								case 'text':
																									return (
																										<input
																											placeholder="-"
																											defaultValue={value}
																											className={classNames(
																												'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
																											)}
																											onBlur={(e) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	e.target
																																		.value,
																															},
																														],
																													},
																													() => {
																														refreshContract(
																															{ id: open?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																										/>
																									);
																								case 'textarea':
																									return (
																										<textarea
																											className={classNames(
																												'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
																											)}
																											placeholder="-"
																											height="auto"
																											rows={3}
																											defaultValue={value}
																											onBlur={(e) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	e.target
																																		.value,
																															},
																														],
																													},
																													() => {
																														refreshContract(
																															{ id: open?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																										/>
																									);
																								case 'numeric':
																									return (
																										<input
																											placeholder="-"
																											defaultValue={value}
																											className={classNames(
																												'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
																											)}
																											onBlur={(e) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	e.target
																																		.value,
																															},
																														],
																													},
																													() => {
																														refreshContract(
																															{ id: open?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																											type="number"
																											pattern="^-?[0-9]\d*\.?\d*$"
																										/>
																									);
																								case 'location':
																									return (
																										<div className="relative w-full">
																											<div
																												className={classNames(
																													'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
																												)}
																												onClick={() => {
																													openSelectLocation({
																														locations,
																														onSelect: (
																															selected,
																														) => {
																															onUpdateContract(
																																{
																																	id: contract?.id,
																																	customAttributes:
																																		[
																																			{
																																				fieldId:
																																					field?.id,
																																				value:
																																					selected?.id,
																																			},
																																		],
																																},
																																(
																																	payload,
																																	err,
																																) => {
																																	refreshContract(
																																		{
																																			id: contract?.id,
																																		},
																																		null,
																																		{
																																			silent: true,
																																		},
																																	);
																																},
																															);
																														},
																														selected: value,
																													});
																												}}
																											>
																												{findPathById(
																													locationOptionsTree,
																													value,
																												)
																													?.map((o) => o.name)
																													?.reverse()
																													.map(
																														(name, index) => (
																															<span
																																className={
																																	index === 0
																																		? 'text-black'
																																		: 'text-gray-400 ml-3'
																																}
																															>
																																{name}
																															</span>
																														),
																													) || '-'}
																											</div>
																											{value &&
																												value !== '-' && (
																													<XMarkIcon
																														className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
																														onClick={() => {
																															onUpdateContract(
																																{
																																	id: contract?.id,
																																	customAttributes:
																																		[
																																			{
																																				fieldId:
																																					field?.id,
																																				value:
																																					null,
																																			},
																																		],
																																},
																																(
																																	payload,
																																	err,
																																) => {
																																	refreshContract(
																																		{
																																			id: contract?.id,
																																		},
																																		null,
																																		{
																																			silent: true,
																																		},
																																	);
																																},
																															);
																														}}
																													/>
																												)}
																										</div>
																									);
																								case 'category':
																									return (
																										<ReactSelect
																											unstyled
																											backspaceRemovesValue={
																												false
																											}
																											styles={{
																												control: (provided) => {
																													return {
																														...provided,
																														outline: '',
																														cursor: 'pointer',
																													};
																												},
																												option: (
																													provided,
																													state,
																												) => {
																													return {
																														...provided,
																														paddingLeft: `${
																															(state.data
																																.depth || 0) *
																																16 +
																															10
																														}px`,
																													};
																												},
																											}}
																											classNames={{
																												control: ({
																													isFocused,
																												}) =>
																													classNames(
																														isFocused &&
																															'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																														'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																													),
																												input: ({
																													isFocused,
																												}) => '',
																												valueContainer: () =>
																													'',
																												placeholder: () =>
																													'py-2.5',
																												menu: () =>
																													'shadow-md border border-gray-100 bg-white',
																												option: ({
																													isFocused,
																													isSelected,
																													isGroup,
																												}) =>
																													classNames(
																														isFocused &&
																															'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																														isSelected &&
																															'hover:border-indigo-500 !border-indigo-500 ',
																														'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																													),
																												noOptionsMessage: () =>
																													'py-4',
																											}}
																											placeholder="-"
																											noOptionsMessage={() =>
																												'No category found'
																											}
																											components={{
																												ClearIndicator,

																												...(value
																													? {
																															DropdownIndicator:
																																() => null,
																														}
																													: {
																															DropdownIndicator,
																														}),
																												MenuList: (props) => (
																													<CustomMenuList
																														{...props}
																														customAction={{
																															label:
																																'Add a new category',
																															onClick: () =>
																																openNewCategory(
																																	{
																																		moduleId:
																																			ASSETS_MODULE_ID,
																																		onSubmit: (
																																			newCategory,
																																		) =>
																																			onUpdateContract(
																																				{
																																					id: contract?.id,
																																					customAttributes:
																																						[
																																							{
																																								fieldId:
																																									field?.id,
																																								value:
																																									newCategory?.value,
																																							},
																																						],
																																				},
																																				(
																																					payload,
																																				) => {
																																					refreshContract(
																																						{
																																							id: contract?.id,
																																						},
																																						null,
																																						{
																																							silent: true,
																																						},
																																					);
																																				},
																																			),
																																	},
																																),
																														}}
																													/>
																												),
																											}}
																											value={contractCategoryOptions?.find(
																												(o) =>
																													o.value === value,
																											)}
																											isDisabled={
																												isLoadingCategories ===
																												'pending'
																											}
																											options={
																												contractCategoryOptions
																											}
																											onChange={(newValue) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	newValue?.value ||
																																	null,
																															},
																														],
																													},
																													(payload) => {
																														refreshContract(
																															{
																																id: contract?.id,
																															},
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																											isClearable
																										/>
																									);
																								case 'boolean':
																									return (
																										<ReactSelect
																											unstyled
																											backspaceRemovesValue={
																												false
																											}
																											styles={{
																												control: (provided) => {
																													return {
																														...provided,
																														outline: '',
																														cursor: 'pointer',
																													};
																												},
																												option: (
																													provided,
																													state,
																												) => {
																													return {
																														...provided,
																														paddingLeft: `${
																															(state.data
																																.depth || 0) *
																																16 +
																															10
																														}px`,
																													};
																												},
																											}}
																											classNames={{
																												control: ({
																													isFocused,
																												}) =>
																													classNames(
																														isFocused &&
																															'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																														'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																													),
																												input: ({
																													isFocused,
																												}) => '',
																												valueContainer: () =>
																													'',
																												placeholder: () =>
																													'py-2.5',
																												menu: () =>
																													'shadow-md border border-gray-100 bg-white',
																												option: ({
																													isFocused,
																													isSelected,
																													isGroup,
																												}) =>
																													classNames(
																														isFocused &&
																															'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																														isSelected &&
																															'hover:border-indigo-500 !border-indigo-500 ',
																														'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																													),
																												noOptionsMessage: () =>
																													'py-4',
																											}}
																											placeholder="-"
																											isSearchable={false}
																											components={{
																												ClearIndicator,

																												...(value
																													? {
																															DropdownIndicator:
																																() => null,
																														}
																													: {
																															DropdownIndicator,
																														}),
																											}}
																											value={booleanOptions?.find(
																												(o) =>
																													o.value === value,
																											)}
																											options={booleanOptions}
																											onChange={(newValue) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	newValue?.value ||
																																	null,
																															},
																														],
																													},
																													() => {
																														refreshContract(
																															{ id: open?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																											isClearable
																										/>
																									);
																								case 'vendor':
																									return (
																										<ReactSelect
																											unstyled
																											backspaceRemovesValue={
																												false
																											}
																											styles={{
																												control: (provided) => {
																													return {
																														...provided,
																														outline: '',
																														cursor: 'pointer',
																													};
																												},
																												option: (
																													provided,
																													state,
																												) => {
																													return {
																														...provided,
																														paddingLeft: `${
																															(state.data
																																.depth || 0) *
																																16 +
																															10
																														}px`,
																													};
																												},
																											}}
																											classNames={{
																												control: ({
																													isFocused,
																												}) =>
																													classNames(
																														isFocused &&
																															'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																														'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																													),
																												input: ({
																													isFocused,
																												}) => '',
																												valueContainer: () =>
																													'',
																												placeholder: () =>
																													'py-2.5',
																												menu: () =>
																													'shadow-md border border-gray-100 bg-white',
																												option: ({
																													isFocused,
																													isSelected,
																													isGroup,
																												}) =>
																													classNames(
																														isFocused &&
																															'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																														isSelected &&
																															'hover:border-indigo-500 !border-indigo-500 ',
																														'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																													),
																												noOptionsMessage: () =>
																													'py-4',
																											}}
																											placeholder="-"
																											noOptionsMessage={() =>
																												'Not found'
																											}
																											components={{
																												ClearIndicator,

																												...(value
																													? {
																															DropdownIndicator:
																																() => null,
																														}
																													: {
																															DropdownIndicator,
																														}),
																												MenuList: (props) => (
																													<CustomMenuList
																														{...props}
																														customAction={{
																															label:
																																'Add a vendor',
																															onClick: () =>
																																openNewVendor({
																																	onSubmit: (
																																		vendor,
																																	) => {
																																		// Refresh vendor list to include newly added vendor
																																		refreshVendors();
																																		onUpdateContract(
																																			{
																																				id: contract?.id,
																																				customAttributes:
																																					[
																																						{
																																							fieldId:
																																								field?.id,
																																							value:
																																								vendor?.value,
																																						},
																																					],
																																			},
																																			(
																																				payload,
																																			) => {
																																				refreshContract(
																																					{
																																						id: contract?.id,
																																					},
																																					null,
																																					{
																																						silent: true,
																																					},
																																				);
																																			},
																																		);
																																	},
																																}),
																														}}
																													/>
																												),
																											}}
																											value={vendorOptions?.find(
																												(o) =>
																													o.value === value,
																											)}
																											isDisabled={
																												isLoadingVendors ===
																												'pending'
																											}
																											options={vendorOptions}
																											onChange={(newValue) => {
																												onUpdateContract(
																													{
																														id: contract?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	newValue?.value ||
																																	null,
																															},
																														],
																													},
																													(payload) => {
																														refreshContract(
																															{
																																id: contract?.id,
																															},
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											}}
																											isClearable
																										/>
																									);
																							}
																						})()
																					: null}
																			</div>
																		</div>
																	</li>
																</>
															);
														})
												: null}
										</ul>
									</div>
								</div>

								{/* Covered Assets */}
								<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center justify-between mb-4">
										<div className="flex items-center gap-2">
											<CubeIcon className="h-5 w-5 text-gray-400" />
											<h3 className="text-base font-semibold text-gray-900">
												Covered Assets
											</h3>
											<span className="ml-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
												{contract?.assets?.length || 0}
											</span>
										</div>
										<Button
											type="secondary"
											Icon={PlusIcon}
											onClick={() => setAddAssetModal(true)}
										>
											Add Asset
										</Button>
									</div>

									{contract?.assets?.length ? (
										<div className="space-y-2">
											{contract.assets.map((asset) => (
												<div
													key={asset.id}
													className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
													onClick={() => {
														handleClose(true);
														navigate(`/assets/${asset.id}/overview`);
													}}
												>
													<div className="flex items-center gap-3">
														<div>
															<div className="font-medium text-gray-900 text-sm">
																{[asset.manufacturer?.name, asset.model?.name]
																	.filter(Boolean)
																	.join(' ') || 'Unnamed Asset'}
															</div>
															<div className="text-sm text-gray-500 text-xs">
																{assetDisplayTemplate
																	? parseAssetTemplate(
																			assetDisplayTemplate,
																			asset,
																		)
																	: `SN ${asset.serialNumber || 'N/A'} · ${asset.location?.name || 'No Location'}`}
															</div>
														</div>
													</div>
													<div className="flex items-center gap-2">
														<ChevronRightIcon className="h-5 w-5 text-gray-400" />
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="text-center py-8 text-gray-500">
											<CubeIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
											<p>No assets attached to this contract</p>
										</div>
									)}
								</div>

								{/* Documents */}
								<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center justify-between mb-4">
										<div className="flex items-center gap-2">
											<DocumentTextIcon className="h-5 w-5 text-gray-400" />
											<h3 className="text-base font-semibold text-gray-900">
												Documents
											</h3>
											<span className="ml-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
												{contract?.documents?.length || 0}
											</span>
										</div>
										<AddAttachmentButton
											buttonText="Upload"
											onUpload={(files) => {
												if (files?.[0]?.fromLibrary) {
													// Library document - add directly
													onAddDocument(
														{
															id: contract?.id,
															documentId: files[0].id,
															setActivePreview: true,
														},
														() => {
															refreshContract({ id: open?.id }, null, {
																silent: true,
															});
														},
														{ silent: true },
													);
												} else {
													// Uploaded file - open modal for category selection
													setDocumentUploadForm({ files });
													setAddDocumentToContract(true);
												}
											}}
											multiple={false}
											libraryMultiSelect={false}
										/>
									</div>

									{contract?.documents?.length ? (
										<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
											{contract.documents.map((doc) => (
												<li
													key={doc.id}
													className="flex items-center justify-between py-3 pl-4 pr-4 text-xs hover:bg-gray-50"
												>
													<div
														className="flex items-center min-w-0 flex-1 cursor-pointer"
														onClick={() =>
															openDocumentPreview({
																document: doc,
																enableEdit: true,
																onEdit: (document) => {
																	closeDocumentPreview();
																	openUploadDocuments({
																		id: document.id,
																		moduleId: DOCUMENTS_MODULE_ID,
																		onSubmit: () => {
																			refreshContract({ id: open?.id }, null, {
																				silent: true,
																			});
																		},
																	});
																},
																onRemove: () => {
																	onRemoveDocument(
																		{
																			id: contract?.id,
																			documentId: doc?.id,
																		},
																		() => {
																			refreshContract({ id: open?.id }, null, {
																				silent: true,
																			});
																		},
																	);
																},
															})
														}
													>
														<PaperClipIcon className="h-4 w-4 flex-shrink-0 text-gray-400 mr-3" />
														<div className="min-w-0 flex-1">
															<p className="truncate font-medium text-gray-900">
																{doc.name || doc.originalName || 'Untitled'}
															</p>
															<p className="text-xs text-gray-500">
																{doc.category?.name && (
																	<span className="mr-2">
																		{doc.category.name}
																	</span>
																)}
																{moment(doc.createdAt).format('MMM D, YYYY')}
															</p>
														</div>
													</div>
													<Menu as="div" className="relative">
														<Menu.Button className="p-1 hover:bg-gray-100 rounded-full">
															<EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
														</Menu.Button>
														<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
															<div className="py-1">
																<Menu.Item>
																	{({ active }) => (
																		<button
																			onClick={() =>
																				openDocumentPreview({
																					document: doc,
																					enableEdit: true,
																					onEdit: (document) => {
																						closeDocumentPreview();
																						openUploadDocuments({
																							id: document.id,
																							moduleId: DOCUMENTS_MODULE_ID,
																							onSubmit: () => {
																								refreshContract(
																									{ id: open?.id },
																									null,
																									{ silent: true },
																								);
																							},
																						});
																					},
																					onRemove: () => {
																						onRemoveDocument(
																							{
																								id: contract?.id,
																								documentId: doc?.id,
																							},
																							() => {
																								refreshContract(
																									{ id: open?.id },
																									null,
																									{ silent: true },
																								);
																							},
																						);
																					},
																				})
																			}
																			className={classNames(
																				active ? 'bg-gray-100' : '',
																				'block w-full text-left px-4 py-2 text-sm text-gray-700',
																			)}
																		>
																			View Document
																		</button>
																	)}
																</Menu.Item>
																<Menu.Item>
																	{({ active }) => (
																		<button
																			onClick={() => {
																				onRemoveDocument(
																					{
																						id: contract?.id,
																						documentId: doc?.id,
																					},
																					() => {
																						refreshContract(
																							{ id: open?.id },
																							null,
																							{ silent: true },
																						);
																					},
																				);
																			}}
																			className={classNames(
																				active ? 'bg-gray-100' : '',
																				'block w-full text-left px-4 py-2 text-sm text-red-600',
																			)}
																		>
																			Remove from Contract
																		</button>
																	)}
																</Menu.Item>
															</div>
														</Menu.Items>
													</Menu>
												</li>
											))}
										</ul>
									) : (
										<div className="text-center py-6 text-gray-500">
											<DocumentTextIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
											<p className="text-sm">No documents uploaded</p>
										</div>
									)}
								</div>
							</div>

							{/* Right Column - 1/3 width */}
							<div className="space-y-6">
								{/* Renewal & Reminders */}

								{/*<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center gap-2 mb-4">
										<BellIcon className="h-5 w-5 text-gray-400" />
										<h3 className="text-base font-semibold text-gray-900">
											Renewal & Reminders
										</h3>
									</div>

									<div className="mt-4">
										{reminders?.length ? (
											<div className="space-y-3">
												{reminders.map((reminder) => (
													<div
														key={reminder.id}
														className="flex items-center gap-3"
													>
														<div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
															<BellIcon className="h-4 w-4 text-indigo-600" />
														</div>
														<div className="flex-1">
															<div className="text-sm text-gray-900">
																{reminder.title ||
																	`${moment.utc(contract?.endDate).diff(moment.utc(reminder.scheduleAt), 'days')} days before expiry`}
															</div>
															<div className="text-xs text-gray-500">
																{moment.utc(reminder.scheduleAt).format(
																	'MMM D, YYYY',
																)}
															</div>
														</div>
													</div>
												))}
											</div>
										) : (
											<p className="text-sm text-gray-500">
												No reminders scheduled
											</p>
										)}
									</div>
								</div>*/}

								{/* Work Orders */}
								<div className="bg-white rounded-lg border border-gray-200">
									<div className="flex items-center justify-between p-4 border-b border-gray-200">
										<div className="flex items-center gap-2">
											<ClipboardDocumentListIcon className="h-5 w-5 text-gray-400" />
											<h3 className="text-base font-semibold text-gray-900">
												Work Orders
											</h3>
										</div>
										<Button
											type="secondary"
											size="sm"
											Icon={PlusIcon}
											onClick={() =>
												openCreateWorkOrder({
													contractId: contract?.id,
													onSuccess: () => {
														refreshContract({ id: open?.id }, null, {
															silent: true,
														});
													},
												})
											}
										>
											Add
										</Button>
									</div>

									{/* Active Work Orders */}
									{contract?.workOrders?.filter(
										(wo) => !['completed', 'cancelled'].includes(wo.status),
									)?.length > 0 ? (
										<div>
											<div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
												<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
													Active
												</span>
											</div>
											<ul className="divide-y divide-gray-100 m-0 p-0">
												{contract?.workOrders
													?.filter(
														(wo) =>
															!['completed', 'cancelled'].includes(wo.status),
													)
													?.slice(0, 5)
													.map((workOrder) => (
														<li
															key={workOrder.id}
															className="flex items-center justify-between py-3 px-4 text-xs hover:bg-gray-50 cursor-pointer"
															onClick={() =>
																openWorkOrderOverview({
																	id: workOrder?.id,
																	onClose: (path) => {
																		if (path) {
																			setClose();
																		}
																	},
																})
															}
														>
															<div className="flex items-center min-w-0 flex-1">
																<div className="min-w-0 flex-1">
																	<p className="truncate font-medium text-gray-900">
																		{workOrder?.title || 'Untitled'}
																	</p>
																	{workOrder?.dueDate && (
																		<p className="text-xs text-gray-500">
																			Due{' '}
																			{moment(workOrder.dueDate).format(
																				'MMM D, YYYY',
																			)}
																		</p>
																	)}
																</div>
															</div>
															<div className="flex items-center gap-2">
																<StatusBadge
																	size="xs"
																	status={workOrder?.status}
																/>
																<ChevronRightIcon className="h-4 w-4 text-gray-400" />
															</div>
														</li>
													))}
											</ul>
										</div>
									) : (
										<div className="px-4 py-4 text-center">
											<p className="text-sm text-gray-500">
												No active work orders
											</p>
										</div>
									)}

									{/* Past Work Orders */}
									{contract?.workOrders?.filter((wo) =>
										['completed', 'cancelled'].includes(wo.status),
									)?.length > 0 && (
										<div>
											<div className="px-4 py-2 bg-gray-50 border-b border-gray-100 border-t">
												<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
													Past
												</span>
											</div>
											<ul className="divide-y divide-gray-100 m-0 p-0">
												{contract?.workOrders
													?.filter((wo) =>
														['completed', 'cancelled'].includes(wo.status),
													)
													?.slice(0, 3)
													.map((workOrder) => (
														<li
															key={workOrder.id}
															className="flex items-center justify-between py-3 px-4 text-xs hover:bg-gray-50 cursor-pointer"
															onClick={() =>
																openWorkOrderOverview({
																	id: workOrder?.id,
																	onClose: (path) => {
																		if (path) {
																			setClose();
																		}
																	},
																})
															}
														>
															<div className="flex items-center min-w-0 flex-1">
																<div className="min-w-0 flex-1">
																	<p className="truncate font-medium text-gray-900">
																		{workOrder?.title || 'Untitled'}
																	</p>
																	{workOrder?.completedDate && (
																		<p className="text-xs text-gray-500">
																			Completed{' '}
																			{moment(workOrder.completedDate).format(
																				'MMM D, YYYY',
																			)}
																		</p>
																	)}
																</div>
															</div>
															<div className="flex items-center gap-2">
																<StatusBadge
																	size="xs"
																	status={workOrder?.status}
																/>
																<ChevronRightIcon className="h-4 w-4 text-gray-400" />
															</div>
														</li>
													))}
											</ul>
										</div>
									)}
								</div>

								{/* PM Schedules */}
								<div className="bg-white rounded-lg border border-gray-200">
									<div className="flex items-center justify-between p-4 border-b border-gray-200">
										<div className="flex items-center gap-2">
											<ClockIcon className="h-5 w-5 text-gray-400" />
											<h3 className="text-base font-semibold text-gray-900">
												PM Schedules
											</h3>
										</div>

										<Menu as="div" className="relative">
											<Menu.Button className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
												<PlusIcon className="h-4 w-4" />
												Add PM Schedule
												<ChevronDownIcon className="h-4 w-4" />
											</Menu.Button>
											<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<div className="py-1">
													<Menu.Item>
														{({ active }) => (
															<button
																onClick={() => {
																	fetchAllPMSchedules();
																	setSelectExistingPMModal(true);
																}}
																className={classNames(
																	active ? 'bg-gray-100' : '',
																	'block w-full text-left px-4 py-2 text-sm text-gray-700',
																)}
															>
																Existing Schedule
															</button>
														)}
													</Menu.Item>
													<Menu.Item>
														{({ active }) => (
															<button
																onClick={() =>
																	openEditPMSchedule({
																		contractId: contract?.id,
																		onSuccess: () => {
																			refreshPMSchedules({
																				contractId: open?.id,
																			});
																		},
																	})
																}
																className={classNames(
																	active ? 'bg-gray-100' : '',
																	'block w-full text-left px-4 py-2 text-sm text-gray-700',
																)}
															>
																New Schedule
															</button>
														)}
													</Menu.Item>
												</div>
											</Menu.Items>
										</Menu>
									</div>
									{pmSchedules?.rows?.length > 0 ? (
										<ul className="divide-y divide-gray-100 m-0 p-0">
											{pmSchedules?.rows?.slice(0, 5).map((schedule) => {
												const nextDue = schedule?.nextDueDate
													? moment(schedule.nextDueDate)
													: null;
												const isOverdue =
													nextDue && nextDue.isBefore(moment(), 'day');

												return (
													<li
														key={schedule.id}
														className="flex items-center justify-between py-3 px-4 text-xs hover:bg-gray-50 cursor-pointer"
														onClick={() =>
															openPMScheduleOverview({
																scheduleId: schedule?.id,
																onClose: (path) => {
																	if (path) {
																		setClose();
																	}
																},
															})
														}
													>
														<div className="flex items-center min-w-0 flex-1">
															<ClockIcon
																className={classNames(
																	'h-4 w-4 flex-shrink-0 mr-3',
																	isOverdue
																		? 'text-red-500'
																		: schedule?.active
																			? 'text-blue-500'
																			: 'text-gray-400',
																)}
															/>
															<div className="min-w-0 flex-1">
																<p className="truncate font-medium text-gray-900">
																	{schedule?.name || 'Untitled Schedule'}
																</p>
																<p className="text-xs text-gray-500">
																	{schedule?.frequency && (
																		<span className="mr-2 capitalize">
																			{schedule.frequency.replace('_', ' ')}
																		</span>
																	)}
																	{schedule?.activeWorkOrder ? (
																		<span className="text-blue-600 text-xxs">
																			{schedule.activeWorkOrder.workOrderCode}{' '}
																			(due{' '}
																			{moment(
																				schedule.activeWorkOrder.dueDate,
																			).format('MMM D')}
																			)
																		</span>
																	) : nextDue ? (
																		<span
																			className={classNames(
																				isOverdue ? 'text-red-600' : '',
																			)}
																		>
																			{isOverdue ? 'Overdue: ' : 'Next: '}
																			{nextDue.format('MMM D, YYYY')}
																		</span>
																	) : null}
																</p>
															</div>
														</div>
														<div className="flex items-center gap-2">
															<StatusBadge
																size="xs"
																status={
																	schedule?.active ? 'active' : 'inactive'
																}
															/>
															<ChevronRightIcon className="h-4 w-4 text-gray-400" />
														</div>
													</li>
												);
											})}
										</ul>
									) : (
										<div className="text-center py-6">
											<ClockIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
											<p className="text-sm text-gray-500">
												No PM schedules linked to this contract
											</p>
										</div>
									)}
								</div>

								{/* Timeline */}
								{/*<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center gap-2 mb-4">
										<ClockIcon className="h-5 w-5 text-gray-400" />
										<h3 className="text-base font-semibold text-gray-900">
											Timeline
										</h3>
									</div>

									{allEvents?.length ? (
										<div className="space-y-4">
											{allEvents.slice(0, 5).map((event, index) => (
												<div key={event.id || index} className="flex gap-3">
													<div className="flex flex-col items-center">
														<div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
															<ClockIcon className="h-4 w-4 text-indigo-600" />
														</div>
														{index < Math.min(allEvents.length - 1, 4) && (
															<div className="w-0.5 h-full bg-gray-200 mt-2" />
														)}
													</div>
													<div className="flex-1 pb-4">
														<div className="text-sm font-medium text-gray-900">
															{event.title || 'Event'}
														</div>
														<div className="text-xs text-gray-500">
															{moment(
																event.completedDate ||
																	event.scheduledDate ||
																	event.estimatedDate,
															).format('MMM D, YYYY')}
														</div>
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="text-center py-6 text-gray-500">
											<ClockIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
											<p className="text-sm">No timeline events</p>
										</div>
									)}
								</div>*/}
							</div>
						</div>
					</div>
				</div>
			</StackableModal>

			<StackableModal
				title="Attach document to contract"
				open={addDocumentToContract}
				onClose={() => setAddDocumentToContract(false)}
				size="md"
			>
				{documentUploadForm?.files?.length ? (
					<div className="flex justify-center mb-10">
						<Document
							file={documentUploadForm?.files[0]}
							onLoadSuccess={() => {}}
						>
							<Page pageNumber={1} width={250} />
						</Document>
					</div>
				) : (
					<>
						<TemplateFile
							onChange={(index, files) => {
								setDocumentUploadForm({ ...documentUploadForm, files });
							}}
						/>

						<TemplateReactSelect
							name={'or choose an existing document'}
							placeholder="Select an existing document"
							options={
								documents?.rows
									?.filter((document) => {
										return !contract?.documents.some(
											(item) => item.id === document.id,
										);
									})
									.map((document) => ({
										label: document?.name,
										value: document?.id,
									})) || []
							}
							onChange={(index, document) => {
								onAddDocument(
									{
										id: contract?.id,
										documentId: document?.value,
										setActivePreview:
											typeof documentUploadForm?.setActivePreview ===
											'undefined'
												? true
												: documentUploadForm?.setActivePreview,
									},
									() => {
										refreshContract({ id: open?.id }, null, {
											silent: true,
										});
										setAddDocumentToContract(false);
									},
									{ silent: true },
								);
							}}
							disabled={isLoadingCategories === 'pending'}
						/>
					</>
				)}

				{documentUploadForm?.files?.length && (
					<>
						<div className={classNames('flex h-fit')}>
							<div className="flex-1">
								<TemplateReactSelect
									name={'Document Category'}
									required={true}
									options={categoryOptions}
									value={
										documentUploadForm?.categoryId
											? categoryOptions?.find(
													(o) => o.value === documentUploadForm?.categoryId,
												)
											: null
									}
									onChange={(index, category) => {
										setDocumentUploadForm({
											...documentUploadForm,
											categoryId: category?.value,
										});
									}}
									disabled={isLoadingCategories === 'pending'}
								/>
							</div>
							{documentUploadForm?.categoryId ? (
								<div className="flex-shrink flex items-center pt-7 ml-3">
									<XCircleIcon
										className="w-6 h-6 cursor-pointer text-gray-500"
										onClick={() => {
											setDocumentUploadForm({
												...documentUploadForm,
												categoryId: undefined,
											});
										}}
									/>
								</div>
							) : (
								<div className="flex-shrink flex items-center pt-7 ml-3">
									<PlusCircleIcon
										className="w-6 h-6 cursor-pointer text-gray-500"
										onClick={() =>
											setAddCategoryOpen({
												moduleId: DOCUMENTS_MODULE_ID,
												onSubmit: (category) => {
													setDocumentUploadForm({
														...documentUploadForm,
														categoryId: category?.value,
													});

													onRefreshCategories(
														{
															id: DOCUMENTS_MODULE_ID,
														},
														null,
														{ silent: true },
													);
												},
											})
										}
									/>
								</div>
							)}
						</div>
					</>
				)}

				<TemplateBoolean
					name="Set as Preview"
					className="mt-6"
					value={
						typeof documentUploadForm?.setActivePreview === 'undefined'
							? true
							: documentUploadForm?.setActivePreview
					}
					onChange={(index, setActivePreview) =>
						setDocumentUploadForm({
							...documentUploadForm,
							setActivePreview,
						})
					}
				/>

				<div className="flex justify-end mt-4">
					<Button
						disabled={
							!documentUploadForm?.files?.length ||
							!documentUploadForm?.categoryId
						}
						onClick={() => {
							onUploadDocuments(
								{
									files: documentUploadForm?.files,
									bucket: 'local-contracts',
									information: {
										id: contract?.id,
										categoryId: documentUploadForm?.categoryId,
										setActivePreview:
											typeof documentUploadForm?.setActivePreview ===
											'undefined'
												? true
												: documentUploadForm?.setActivePreview,
									},
								},
								() => {
									refreshContract({ id: open?.id });
									setDocumentUploadForm({});
									setAddDocumentToContract(false);
								},
							);
						}}
					>
						{isUploadingDocument === 'pending' ? 'Uploading...' : 'Upload'}
					</Button>
				</div>
			</StackableModal>

			<StackableModal
				title="Add assets to contract"
				open={addAssetModal}
				onClose={() => setAddAssetModal(false)}
				size="lg"
			>
				<div className="space-y-4 py-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Select Assets
						</label>
						<AssetSelectorBeta
							value={selectedAssetsToAdd}
							onChange={setSelectedAssetsToAdd}
							assets={assets}
							placeholder="Select assets..."
							multiple={true}
							allowGroups={false}
						/>
					</div>
					<div className="flex justify-end gap-3 pt-4">
						<Button
							color="white"
							type="secondary"
							onClick={() => setAddAssetModal(false)}
						>
							Cancel
						</Button>
						<Button
							color="primary"
							disabled={
								isAddingAssetToContract === 'pending' ||
								selectedAssetsToAdd.length === 0
							}
							onClick={() =>
								onAddAssetToContract(
									{ id: open?.id, assetIds: selectedAssetsToAdd },
									() => {
										setAddAssetModal(false);
									},
								)
							}
						>
							{isAddingAssetToContract === 'pending'
								? 'Adding...'
								: `Add ${selectedAssetsToAdd.length || ''} Asset${selectedAssetsToAdd.length !== 1 ? 's' : ''}`}
						</Button>
					</div>
				</div>
			</StackableModal>

			<StackableModal
				open={!!confirmModal}
				onClose={() => {
					setRenewForm({});
					setConfirmModal(null);
				}}
				title="Review & Renew Contract"
				size="2xl"
			>
				<div className="space-y-6 pt-4">
					{/* Document Upload Section */}
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h4 className="text-sm font-medium text-gray-900">
								Upload New Contract Document
							</h4>
							<span className="text-xs text-gray-500">(Optional)</span>
						</div>

						<FileUpload
							files={renewForm?.files || []}
							onChange={(files) => {
								setRenewForm({ ...renewForm, files });
							}}
							multiple={false}
							acceptTypes="application/pdf"
							buttonText="Add Document"
							showLibraryOption
							libraryFileTypes={['application/pdf']}
						/>

						{/* Only show category field for newly uploaded files, not library files */}
						{renewForm?.files?.length > 0 &&
							!renewForm?.files?.[0]?.fromLibrary && (
								<div className="space-y-4 pl-1">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1.5">
											Document Category <span className="text-red-500">*</span>
										</label>
										<div className="flex items-center gap-2">
											<div className="flex-1">
												<FilterDropdown
													placeholder="Select category"
													value={renewForm?.categoryId}
													options={
														categoryOptions?.map((opt) => ({
															value: opt.value,
															label: opt.label,
														})) || []
													}
													onChange={(categoryId) => {
														setRenewForm({
															...renewForm,
															categoryId,
														});
													}}
													disabled={isLoadingCategories === 'pending'}
													clearable={true}
													size="md"
													variant="outline"
													className="w-full"
												/>
											</div>
											<button
												type="button"
												onClick={() =>
													setAddCategoryOpen({
														moduleId: DOCUMENTS_MODULE_ID,
														onSubmit: (category) => {
															setRenewForm({
																...renewForm,
																categoryId: category?.value,
															});
															onRefreshCategories(
																{ id: DOCUMENTS_MODULE_ID },
																null,
																{ silent: true },
															);
														},
													})
												}
												className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
											>
												<PlusCircleIcon className="w-5 h-5" />
											</button>
										</div>
									</div>
								</div>
							)}
					</div>

					{/* Contract End Date Section */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1.5">
							New Contract End Date
						</label>
						<DatePicker
							value={renewForm?.endDate}
							onChange={(endDate) => setRenewForm({ ...renewForm, endDate })}
							minDate={contract?.endDate}
							placeholder="Select new end date"
						/>
					</div>

					{/* PM Schedules Section */}
					<div>
						<div className="flex items-center justify-between mb-3">
							<h4 className="text-sm font-medium text-gray-900">
								PM Schedules
							</h4>
							<Button
								type="secondary"
								size="sm"
								Icon={PlusIcon}
								onClick={() =>
									openEditPMSchedule({
										contractId: contract?.id,
										onSuccess: () => {
											refreshPMSchedules({ contractId: open?.id });
										},
									})
								}
							>
								Add Schedule
							</Button>
						</div>
						{pmSchedules?.rows?.length > 0 ? (
							<ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
								{pmSchedules?.rows?.map((schedule) => {
									const nextDue = schedule?.nextDueDate
										? moment(schedule.nextDueDate)
										: null;
									const isOverdue =
										nextDue && nextDue.isBefore(moment(), 'day');

									return (
										<li
											key={schedule.id}
											className="flex items-center justify-between py-3 px-4 hover:bg-gray-50"
										>
											<div className="flex items-center min-w-0 flex-1">
												<ClockIcon
													className={classNames(
														'h-5 w-5 flex-shrink-0 mr-3',
														isOverdue
															? 'text-red-500'
															: schedule?.active
																? 'text-blue-500'
																: 'text-gray-400',
													)}
												/>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900 truncate">
														{schedule?.name || 'Untitled Schedule'}
													</p>
													<p className="text-xs text-gray-500">
														{schedule?.frequency && (
															<span className="capitalize">
																{schedule.frequency.replace('_', ' ')}
															</span>
														)}
														{schedule?.activeWorkOrder ? (
															<span className="ml-2 text-blue-600 text-xxs">
																{schedule.activeWorkOrder.workOrderCode} (due{' '}
																{moment(
																	schedule.activeWorkOrder.dueDate,
																).format('MMM D')}
																)
															</span>
														) : nextDue ? (
															<span
																className={classNames(
																	'ml-2',
																	isOverdue ? 'text-red-600' : '',
																)}
															>
																{isOverdue ? 'Overdue: ' : 'Next: '}
																{nextDue.format('MMM D, YYYY')}
															</span>
														) : null}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<StatusBadge
													size="xs"
													status={schedule?.active ? 'active' : 'inactive'}
												/>
											</div>
										</li>
									);
								})}
							</ul>
						) : (
							<div className="text-center py-6 border border-gray-200 rounded-lg bg-gray-50">
								<ClockIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
								<p className="text-sm text-gray-500">
									No PM schedules linked to this contract
								</p>
							</div>
						)}
					</div>

					{/* Footer Actions */}
					<div className="flex justify-end gap-3 pt-2">
						<Button
							type="secondary"
							onClick={() => {
								setRenewForm({});
								setConfirmModal(null);
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={
								// Only require categoryId for uploaded files, not library files
								renewForm?.files?.length > 0 &&
								!renewForm?.files?.[0]?.fromLibrary &&
								!renewForm?.categoryId
							}
							onClick={async () => {
								// Separate uploaded files from library files
								const uploadedFiles = (renewForm?.files || [])
									.filter((f) => !f.fromLibrary && f.original)
									.map((f) => f.original);
								const libraryDocs = (renewForm?.files || []).filter(
									(f) => f.fromLibrary && f.id,
								);

								const onComplete = () => {
									refreshContract({ id: open?.id }, null, {
										silent: true,
									});
									refreshPMSchedules({ contractId: open?.id });
									setConfirmModal(false);
								};

								// Handle library documents - use PUT endpoint
								if (libraryDocs.length > 0) {
									for (const doc of libraryDocs) {
										await onAddDocument({
											id: contract?.id,
											documentId: doc.id,
											setActivePreview:
												typeof renewForm?.setActivePreview === 'undefined'
													? true
													: renewForm?.setActivePreview,
										});
									}
									// Update end date if provided
									if (renewForm?.endDate) {
										await handleUpdateContract({
											id: contract?.id,
											endDate: renewForm?.endDate,
										});
									}
									onComplete();
								}
								// Handle uploaded files - use POST endpoint
								else if (uploadedFiles.length > 0) {
									onUploadDocuments(
										{
											id: contract?.id,
											files: uploadedFiles,
											bucket: 'local-contracts',
											information: {
												id: contract?.id,
												categoryId: renewForm?.categoryId,
												setActivePreview:
													typeof renewForm?.setActivePreview === 'undefined'
														? true
														: renewForm?.setActivePreview,
												endDate: renewForm?.endDate,
											},
										},
										onComplete,
									);
								}
								// No files - just update the contract
								else {
									if (renewForm?.endDate) {
										await handleUpdateContract({
											id: contract?.id,
											endDate: renewForm?.endDate,
										});
									}
									onComplete();
								}
							}}
						>
							Renew Contract
						</Button>
					</div>
				</div>
			</StackableModal>

			<StackableModal
				open={!!reminderModal}
				onClose={() => {
					setReminderModal(null);
				}}
				title="Reminders"
				size="lg"
			>
				<ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-8 xl:gap-x-8">
					{/*clients.map((client) => (
						<>
							<li
								key={client.id}
								className="overflow-hidden rounded-xl border border-gray-100"
							>
								<div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
									<div className="text-sm/6 font-medium text-gray-900">
										Contract Expiring
									</div>
								</div>
								<dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
									<div className="flex justify-between gap-x-4 py-3">
										<dt className="text-gray-500">Created by:</dt>
										<dd className="text-gray-700">
											<div className="font-medium text-gray-900">
												{'Default'}
											</div>
										</dd>
									</div>
									<div className="flex justify-between gap-x-4 py-3">
										<dt className="text-gray-500">Reminder Date</dt>
										<dd className="flex items-start gap-x-2">
											<TemplateDate
												onChange={(index, value, raw) => {}}
											/>
										</dd>
									</div>
								</dl>
							</li>
							<div className="flex justify-end">
								<Button type="danger">Delete Reminder</Button>
							</div>
						</>
					))*/}
				</ul>

				{reminders?.length ? (
					<div className="rounded-md bg-blue-50 p-4">
						<div className="flex">
							<div className="shrink-0">
								<InformationCircleIcon
									aria-hidden="true"
									className="size-5 text-blue-400"
								/>
							</div>
							<div className="ml-3 flex-1 md:flex md:justify-between items-center">
								<p className="text-xs text-blue-700">
									{REMIND_ALL_ADMINS
										? 'Reminders will be sent out to all administrators'
										: REMIND_PRIMARY_USER
											? `Reminders will be sent to ${REMIND_PRIMARY_USER}`
											: `Reminders will be sent to cexa`}
								</p>
								<p
									className="mt-3 text-xs md:ml-6 md:mt-0 cursor-pointer"
									onClick={() => {
										handleClose();
										navigate('/settings/assignments');
									}}
								>
									<div className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600">
										Settings
										<span aria-hidden="true"> &rarr;</span>
									</div>
								</p>
							</div>
						</div>
					</div>
				) : null}
				<div>
					{reminders?.length ? (
						<>
							<ul role="list" className="divide-y mt-3">
								{reminders?.map((reminder) => (
									<li
										key={reminder.id}
										className="relative flex items-center space-x-4 py-4"
									>
										<div className="min-w-0 flex-auto">
											<div className="flex items-center gap-x-5">
												<div
													className={classNames(
														statuses[
															reminder.snoozedUntil ? 'snooze' : 'active'
														],
														'flex-none rounded-full p-1',
													)}
												>
													<div className="size-2 rounded-full bg-current" />
												</div>
												<h2 className="min-w-0 text-xs text-gray-700">
													<a href={reminder.href} className="flex gap-x-2">
														<span className="truncate font-semibold">
															{`${moment(reminder?.date)
																.subtract(reminder?.daysOffset, 'days')
																.format('MM/DD/YYYY')}`}
														</span>
														<span className="text-gray-400">/</span>
														<span className="whitespace-nowrap">
															{`${moment(reminder?.date)
																.subtract(reminder?.daysOffset, 'days')
																.fromNow()}`}
														</span>
													</a>
													<div className="mt-1 flex items-center gap-x-2.5 text-xs/5 text-gray-400">
														<p className="truncate">
															{`Trigger: ${
																reminder?.daysOffset || 30
															} days before expiration`}
														</p>
													</div>
												</h2>
											</div>
										</div>
										<div>
											<Button
												type="danger"
												onClick={() =>
													onOpenDeleteReminder({
														title:
															'Are you sure you want to delete this reminder?',
														confirmText: 'Delete reminder',
														onConfirm: () =>
															onDeleteReminder(reminder?.id, () => {
																onRefreshReminders({ modelId: contract?.id });
																onCloseDeleteReminder();
															}),
													})
												}
											>
												Delete
											</Button>
										</div>
									</li>
								))}
							</ul>
						</>
					) : (
						<p className="py-32 text-gray-500 flex items-center justify-center">
							No reminders
						</p>
					)}
				</div>
			</StackableModal>

			<StackableModal
				open={!!addReminderModal}
				onClose={() => {
					setAddReminderModal(null);
				}}
				title="Add a reminder"
				size="md"
			>
				<div className="space-y-4">
					{/*<TemplateField
						name="Name"
						value={addReminderModal?.name || ''}
						optional
						onChange={(index, name) =>
							setAddReminderModal({ ...addReminderModal, name })
						}
					/>*/}
					<TemplateDropdown
						options={[
							{ label: '7 days', value: 7 },
							{ label: '14 days', value: 14 },
							{ label: '30 days', value: 30 },
							{ label: '60 days', value: 60 },
						]}
						name="Notify"
						value={addReminderModal?.time || 30}
						onChange={(index, time) =>
							setAddReminderModal({ ...addReminderModal, time })
						}
						required
					/>
					<div className="flex justify-end items-center">
						<Button type="secondary" onClick={() => setAddReminderModal(null)}>
							Close
						</Button>
						<Button
							onClick={() => {
								//setAddReminderModal(null);
								onCreateReminder(
									{
										contractId: contract?.id,
										date: contract?.endDate,
										daysOffset: addReminderModal?.time,
										modelId: contract?.id,
										modelName: 'contract',
									},
									() => {
										setAddReminderModal(null);
										onRefreshReminders({ modelId: open?.id });
									},
								);
							}}
						>
							Add Reminder
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Select Existing PM Schedule Modal */}
			<StackableModal
				open={selectExistingPMModal}
				onClose={() => setSelectExistingPMModal(false)}
				title="Link Existing PM Schedule"
				size="lg"
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-500 mb-4">
						Select a PM schedule to link to this contract.
					</p>
					<div className="py-4">
						{allPMSchedules?.rows?.length > 0 ? (
							<ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg max-h-96 overflow-y-auto my-4">
								{allPMSchedules?.rows
									?.filter(
										(schedule) =>
											!schedule.contractId ||
											schedule.contractId !== contract?.id,
									)
									.map((schedule) => (
										<li
											key={schedule.id}
											className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 cursor-pointer"
											onClick={() => {
												// If schedule already has a different contract, ask for confirmation
												if (
													schedule.contractId &&
													schedule.contractId !== contract?.id
												) {
													setConfirmPMSwitch(schedule);
												} else {
													// No existing contract, link directly
													onUpdatePMSchedule(
														{
															id: schedule.id,
															contractId: contract?.id,
														},
														() => {
															refreshPMSchedules({ contractId: open?.id });
															setSelectExistingPMModal(false);
														},
													);
												}
											}}
										>
											<div className="flex items-center min-w-0 flex-1">
												<ClockIcon
													className={classNames(
														'h-5 w-5 flex-shrink-0 mr-3',
														schedule?.active
															? 'text-blue-500'
															: 'text-gray-400',
													)}
												/>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900 truncate">
														{schedule?.name || 'Untitled Schedule'}
													</p>
													<p className="text-xs text-gray-500">
														{schedule?.frequency && (
															<span className="capitalize">
																{schedule.frequency.replace('_', ' ')}
															</span>
														)}
														{schedule?.assets?.length > 0 && (
															<span className="ml-2">
																· {schedule.assets.length} asset
																{schedule.assets.length === 1 ? '' : 's'}
															</span>
														)}
														{schedule?.contractId && (
															<span className="text-gray-600">
																Linked to another contract
															</span>
														)}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2 ml-3">
												<StatusBadge
													size="xs"
													status={schedule?.active ? 'active' : 'inactive'}
												/>
											</div>
										</li>
									))}
							</ul>
						) : (
							<div className="text-center py-8 border border-gray-200 rounded-lg bg-gray-50">
								<ClockIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
								<p className="text-sm text-gray-500">
									No PM schedules available
								</p>
							</div>
						)}
					</div>
					<div className="flex justify-end">
						<Button
							type="secondary"
							onClick={() => setSelectExistingPMModal(false)}
						>
							Cancel
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Confirm PM Schedule Switch Modal */}
			<StackableModal
				open={!!confirmPMSwitch}
				onClose={() => setConfirmPMSwitch(null)}
				title="Move PM Schedule?"
				size="sm"
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-600">
						The PM schedule "
						<span className="font-medium">{confirmPMSwitch?.name}</span>" is
						currently linked to another contract.
					</p>
					<p className="text-sm text-gray-600">
						Are you sure you want to move it to this contract? It will be
						removed from the other contract.
					</p>
					<div className="flex justify-end gap-2 pt-2">
						<Button type="secondary" onClick={() => setConfirmPMSwitch(null)}>
							Cancel
						</Button>
						<Button
							onClick={() => {
								onUpdatePMSchedule(
									{
										id: confirmPMSwitch?.id,
										contractId: contract?.id,
									},
									() => {
										refreshPMSchedules({ contractId: open?.id });
										setConfirmPMSwitch(null);
										setSelectExistingPMModal(false);
									},
								);
							}}
						>
							Move Schedule
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Edit Contract Modal */}
			<StackableModal
				open={!!editContractModal}
				onClose={() => {
					setEditContractModal(false);
					setEditContractForm({});
				}}
				title="Edit Contract"
				size="lg"
			>
				<div className="space-y-4 pt-2">
					<TemplateField
						name="Contract Name"
						value={editContractForm?.name || ''}
						onChange={(index, name) =>
							setEditContractForm({ ...editContractForm, name })
						}
					/>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1.5">
								Start Date
							</label>
							<DatePicker
								value={editContractForm?.startDate}
								onChange={(date) =>
									setEditContractForm({ ...editContractForm, startDate: date })
								}
								size="md"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1.5">
								End Date
							</label>
							<DatePicker
								value={editContractForm?.endDate}
								onChange={(date) =>
									setEditContractForm({ ...editContractForm, endDate: date })
								}
								minDate={editContractForm?.startDate}
								size="md"
							/>
						</div>
					</div>
					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setEditContractModal(false);
								setEditContractForm({});
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								onUpdateContract(
									{
										id: contract?.id,
										name: editContractForm?.name,
										startDate: editContractForm?.startDate,
										endDate: editContractForm?.endDate,
									},
									() => {
										refreshContract({ id: contract?.id }, null, {
											silent: true,
										});
										setEditContractModal(false);
										setEditContractForm({});
									},
								);
							}}
							disabled={!editContractForm?.name}
						>
							Save Changes
						</Button>
					</div>
				</div>
			</StackableModal>
		</>
	);
};

export default ContractOverview;
